import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog"]

  connect() {
    this.previousFocus = null
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  open() {
    this.previousFocus = document.activeElement
    this.dialogTarget.showModal()
    this.dialogTarget.setAttribute("aria-modal", "true")
    this.dialogTarget.setAttribute("role", "dialog")

    // Add keydown listener for focus trap and escape
    this.dialogTarget.addEventListener("keydown", this.handleKeydown)

    // Focus first focusable element
    requestAnimationFrame(() => {
      const focusable = this.getFocusableElements()
      if (focusable.length > 0) {
        focusable[0].focus()
      }
    })
  }

  close() {
    this.dialogTarget.removeEventListener("keydown", this.handleKeydown)
    this.dialogTarget.close()
    this.dialogTarget.removeAttribute("aria-modal")

    // Restore focus to previously focused element
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    // Focus trap - handle Tab key
    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements()
    if (focusable.length === 0) return

    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]

    if (event.shiftKey) {
      // Shift + Tab: go to last element if on first
      if (document.activeElement === firstFocusable) {
        event.preventDefault()
        lastFocusable.focus()
      }
    } else {
      // Tab: go to first element if on last
      if (document.activeElement === lastFocusable) {
        event.preventDefault()
        firstFocusable.focus()
      }
    }
  }

  getFocusableElements() {
    const selector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')

    return Array.from(this.dialogTarget.querySelectorAll(selector))
      .filter(el => el.offsetParent !== null) // Only visible elements
  }

  // Close when clicking backdrop
  clickOutside(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  disconnect() {
    this.dialogTarget?.removeEventListener("keydown", this.handleKeydown)
  }
}
